import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { isFunction } from 'lodash';
import Toggler from '@fiverr/ui_toggle';
import { getRenderable } from '../lib';
import {
    ESCAPE_KEY_CODE,
    ESCAPE_KEY_NAME
} from './constants';
import classes from './index.module.scss';

/**
 * Checks whether the "escape" key was pressed.
 * @param {Event} event The browser event that occured.
 * @return {Boolean}
 */
const pressedEscape = ({
    keyCode,
    which,
    code,
    key
}) => (
    keyCode === ESCAPE_KEY_CODE ||
    which === ESCAPE_KEY_CODE ||
    code === ESCAPE_KEY_NAME ||
    key === ESCAPE_KEY_NAME
);

class Modal extends Toggler {

    constructor(props) {
        super(props);
        this.timeoutIds = [];
        this.openClass = classes.modalOpen;
    }

    componentDidMount() {
        if (this.props.autoOpen) { this.open(); }
    }

    componentWillUnmount() {
        super.componentWillUnmount();
        this.timeoutIds.forEach((id) => clearTimeout(id));
        this.timeoutIds = [];
        this.close();
        this.removeBodyOpenClass();
    }

    get targetClassName() {
        return classnames(
            'modal-package',
            classes.modal,
            this.props.name
        );
    }

    get modalProps() {
        const {
            open,
            close,
            toggle,
            preventCloseAction
        } = this;

        const {
            isOpen
        } = this.state;

        const {
            trigger,
            children,
            ...props
        } = this.props;

        const modalProps = {
            open,
            close,
            toggle,
            preventCloseAction,
            isOpen,
            ...props
        };

        return modalProps;
    }

    onEscape = (e) => {
        if (pressedEscape(e)) {
            this.close();
        }
    }

    bindClickListener = () => {
        if (
            this.props.bindBodyClickListener === false ||
            !this.target
        ) { return; }

        this.target.addEventListener(
            'click',
            this.onBodyClick,
            { capture: true }
        );
    };

    addBodyOpenClass = () => {
        document.body.classList.add(this.openClass);
    };

    removeBodyOpenClass = () => {
        document.body.classList.remove(this.openClass);
    };

    togglerWillOpen() {
        if (this.target) { return; }

        this.target = document.createElement('div');
        this.target.className = this.targetClassName;

        document.body.appendChild(this.target);

        if (this.props.closeOnEscape) {
            window.addEventListener('keyup', this.onEscape);
        }
    }

    togglerDidOpen() {
        this.addBodyOpenClass();

        this.timeoutIds.push(
            setTimeout(() => this.target.classList.add(classes.shown))
        );

        if (isFunction(this.props.onOpen)) {
            this.props.onOpen();
        }
    }

    togglerWillClose() {
        if (!this.target) { return; }

        document.body.removeChild(this.target);
        this.target = undefined;

        window.removeEventListener('keyup', this.onEscape);
    }

    togglerDidClose() {
        this.removeBodyOpenClass();

        if (isFunction(this.props.onClose)) {
            this.props.onClose();
        }
    }

    __view() {
        const {
            children
        } = this.props;

        return getRenderable(children, this.modalProps);
    }

    render() {
        return (
            <>
                {getRenderable(this.props.trigger, this.modalProps)}
                {this.target &&
                    ReactDOM.createPortal(
                        this.__view(this.modalProps),
                        this.target
                    )
                }
            </>
        );
    }
}

Modal.propTypes = {
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    closeOnEscape: PropTypes.bool,
    bindBodyClickListener: PropTypes.bool,
    autoOpen: PropTypes.bool,
    trigger: PropTypes.func,
    name: PropTypes.string
};

Modal.defaultProps = {
    closeOnEscape: true,
    bindBodyClickListener: true
};

export default Modal;
